import { useState, useEffect } from 'react';
import { useMembers, useWorkspace } from '../hooks/useApi';
import { api } from '../api/client';
import { Avatar } from '../components/AppShell';
import { useWorkspaceContext } from '../contexts/WorkspaceContext';
import { SLACK_CLIENT_ID } from '../env';
import type { WorkspaceMemberRole } from '../../../src/shared/types';

function formatRoleLabel(role: string) {
  return role[0].toUpperCase() + role.slice(1);
}

// Roles that can be granted via an invite link. Owner is intentionally
// excluded: ownership is granted only via the per-member role selector on
// the members list (which is owner-only), never via a shareable link. A
// leaked owner-grade link would let anyone take administrative control of
// the workspace.
const INVITABLE_ROLES: WorkspaceMemberRole[] = ['admin', 'editor', 'viewer'];

const ROLE_DESCRIPTIONS: Record<WorkspaceMemberRole, string> = {
  owner: 'Full control of the workspace, including billing and ownership.',
  admin: 'Manage members, settings, and invites; cannot transfer ownership.',
  editor: 'Create and publish artifacts; comment on others.',
  viewer: 'View artifacts and add comments.',
};

export default function Settings() {
  const { user } = useWorkspaceContext();
  const { workspace, loading: wsLoading, refetch: refetchWorkspace } = useWorkspace();
  const { members, loading: membersLoading, refetch: refetchMembers } = useMembers();
  const mcpUrl = import.meta.env.VITE_MCP_URL || 'https://exhibit-mcp.chattermill.app';
  const [copiedMcpUrl, setCopiedMcpUrl] = useState(false);

  const [workspaceName, setWorkspaceName] = useState('');
  const [nameInitialized, setNameInitialized] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<WorkspaceMemberRole>('viewer');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSent, setInviteSent] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [linkGenerating, setLinkGenerating] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [changingRole, setChangingRole] = useState<string | null>(null);
  const [slackStatus, setSlackStatus] = useState<{
    connected: boolean;
    slack_team_name?: string;
    installed_by_name?: string;
    installed_at?: string;
  } | null>(null);
  const [slackDisconnecting, setSlackDisconnecting] = useState(false);

  if (workspace && !nameInitialized) {
    setWorkspaceName(workspace.name);
    setNameInitialized(true);
  }

  const currentMember = members.find((member) => member.id === user.id);
  const currentRole = currentMember?.role ?? 'viewer';
  const canManageWorkspace = currentRole === 'owner' || currentRole === 'admin';
  const canChangeRoles = currentRole === 'owner';
  const loading = wsLoading || membersLoading;

  // Owner is never invitable via a link — see INVITABLE_ROLES at top of file.
  const availableInviteRoles = INVITABLE_ROLES;

  const handleSaveName = async () => {
    if (!workspaceName.trim() || saving || !canManageWorkspace) return;
    setSaving(true);
    try {
      await api.updateWorkspace({ name: workspaceName.trim() });
      refetchWorkspace();
    } finally {
      setSaving(false);
    }
  };

  const handleInvite = async () => {
    if (!canManageWorkspace) return;
    const email = inviteEmail.trim();
    if (!email) return;
    setInviteSending(true);
    setInviteError(null);
    try {
      await api.createInviteLink({ email, role: inviteRole });
      setInviteSent(true);
      setInviteEmail('');
      setTimeout(() => setInviteSent(false), 3000);
      refetchMembers();
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Could not send invite');
    } finally {
      setInviteSending(false);
    }
  };

  const handleGenerateLink = async () => {
    if (!canManageWorkspace) return;
    setLinkGenerating(true);
    setInviteError(null);
    setGeneratedLink(null);
    setLinkCopied(false);
    try {
      const { link } = await api.createInviteLink({ role: inviteRole });
      setGeneratedLink(link);
      try {
        await navigator.clipboard.writeText(link);
        setLinkCopied(true);
      } catch {
        // Clipboard may be unavailable (e.g. http context). Link is still shown.
      }
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Could not generate invite link');
    } finally {
      setLinkGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    if (!generatedLink) return;
    try {
      await navigator.clipboard.writeText(generatedLink);
      setLinkCopied(true);
      window.setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const handleRoleChange = async (memberId: string, role: string) => {
    if (!canChangeRoles) return;
    setChangingRole(memberId);
    try {
      await api.updateMemberRole(memberId, role);
      refetchMembers();
    } finally {
      setChangingRole(null);
    }
  };

  useEffect(() => {
    if (canManageWorkspace) {
      api.getSlackStatus().then(setSlackStatus).catch(() => {});
    }
  }, [canManageWorkspace]);

  const handleDisconnectSlack = async () => {
    setSlackDisconnecting(true);
    try {
      await api.disconnectSlack();
      setSlackStatus({ connected: false });
    } finally {
      setSlackDisconnecting(false);
    }
  };

  const handleCopyMcpUrl = () => {
    navigator.clipboard.writeText(mcpUrl);
    setCopiedMcpUrl(true);
    window.setTimeout(() => setCopiedMcpUrl(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div
          className="w-[32px] h-[32px] border-[3px] rounded-full animate-spin"
          style={{
            borderColor: 'var(--rd-border)',
            borderTopColor: 'var(--rd-text-strong)',
          }}
        />
      </div>
    );
  }

  return (
    <div className="px-[var(--sp-6)] pt-[16px] pb-[var(--sp-8)]">
      <h1
        className="text-[24px] font-bold mb-[var(--sp-2)]"
        style={{ color: 'var(--rd-text-strong)' }}
      >
        Workspace Settings
      </h1>
      <p
        className="text-[14px] mb-[var(--sp-8)]"
        style={{ color: 'var(--rd-text-subtle)' }}
      >
        Manage your workspace, members, and shared organization setup.
      </p>

      <section className="mb-[var(--sp-8)]">
        <h2
          className="text-[16px] font-semibold mb-[var(--sp-4)]"
          style={{ color: 'var(--rd-text-strong)' }}
        >
          General
        </h2>
        <div
          className="rounded-[10px] p-[var(--sp-5)] border"
          style={{
            background: 'var(--rd-surface-panel)',
            borderColor: 'var(--rd-border)',
          }}
        >
          <label
            className="block text-[13px] font-medium mb-[var(--sp-1)]"
            style={{ color: 'var(--rd-text-subtle)' }}
          >
            Workspace name
          </label>
          <div className="flex flex-col sm:flex-row gap-[var(--sp-3)]">
            <input
              type="text"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              disabled={!canManageWorkspace}
              className="flex-1 h-[44px] px-[var(--sp-4)] rounded-[8px] text-[14px] border outline-none transition-colors duration-[120ms] disabled:opacity-70 disabled:cursor-default"
              style={{
                background: 'var(--rd-surface-input)',
                borderColor: 'var(--rd-border)',
                color: 'var(--rd-text-strong)',
              }}
            />
            {canManageWorkspace ? (
              <button
                onClick={handleSaveName}
                disabled={saving || workspaceName === workspace?.name}
                className="h-[44px] px-[var(--sp-4)] rounded-[8px] text-[14px] font-semibold cursor-pointer border-0 text-white transition-colors duration-[120ms] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: 'var(--rd-text-strong)',
                }}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            ) : null}
          </div>
          {!canManageWorkspace && (
            <p
              className="mt-[var(--sp-3)] mb-0 text-[13px]"
              style={{ color: 'var(--rd-text-subtle)' }}
            >
              Only workspace owners and admins can change the workspace name.
            </p>
          )}
        </div>
      </section>

      <section className="mb-[var(--sp-8)]">
        <div className="mb-[var(--sp-4)]">
          <h2
            className="text-[16px] font-semibold"
            style={{ color: 'var(--rd-text-strong)' }}
          >
            Members
          </h2>
          <p
            className="text-[13px] mt-[4px] mb-0"
            style={{ color: 'var(--rd-text-subtle)' }}
          >
            Everyone in the workspace can see this list. Only owners can change roles.
          </p>
        </div>

        {canManageWorkspace ? (
          <div
            className="rounded-[10px] p-[var(--sp-5)] border mb-[var(--sp-4)]"
            style={{
              background: 'var(--rd-surface-panel)',
              borderColor: 'var(--rd-border)',
            }}
          >
            <div className="flex items-baseline justify-between mb-[var(--sp-3)]">
              <h3
                className="text-[14px] font-semibold m-0"
                style={{ color: 'var(--rd-text-strong)' }}
              >
                Invite people
              </h3>
              <span
                className="text-[12px]"
                style={{ color: 'var(--rd-text-subtle)' }}
              >
                The role below applies to anyone who accepts.
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-[var(--sp-2)] mb-[var(--sp-3)]">
              <label
                className="text-[13px] font-medium sm:w-[80px]"
                style={{ color: 'var(--rd-text-subtle)' }}
              >
                Role
              </label>
              <select
                value={inviteRole}
                onChange={(e) => {
                  setInviteRole(e.target.value as WorkspaceMemberRole);
                  // Invalidate any previously generated link since its role no longer matches.
                  setGeneratedLink(null);
                  setLinkCopied(false);
                }}
                className="h-[36px] px-[var(--sp-2)] rounded-[8px] text-[13px] font-semibold border cursor-pointer"
                style={{
                  background: 'var(--rd-surface-input)',
                  borderColor: 'var(--rd-border)',
                  color: 'var(--rd-text-strong)',
                }}
              >
                {availableInviteRoles.map((r) => (
                  <option key={r} value={r}>
                    {formatRoleLabel(r)}
                  </option>
                ))}
              </select>
              <span
                className="text-[12px] flex-1"
                style={{ color: 'var(--rd-text-subtle)' }}
              >
                {ROLE_DESCRIPTIONS[inviteRole]}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-[var(--sp-2)] mb-[var(--sp-3)]">
              <label
                className="text-[13px] font-medium sm:w-[80px]"
                style={{ color: 'var(--rd-text-subtle)' }}
              >
                Email
              </label>
              <input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleInvite();
                }}
                placeholder="someone@example.com"
                className="h-[36px] px-[var(--sp-3)] rounded-[8px] text-[13px] border outline-none flex-1 min-w-0"
                style={{
                  background: 'var(--rd-surface-input)',
                  borderColor: 'var(--rd-border)',
                  color: 'var(--rd-text-strong)',
                }}
              />
              <button
                onClick={handleInvite}
                disabled={inviteSending || !inviteEmail.trim()}
                className="h-[36px] px-[var(--sp-4)] rounded-[8px] text-[13px] font-semibold cursor-pointer border-0 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'var(--rd-accent)' }}
              >
                {inviteSent
                  ? 'Sent!'
                  : inviteSending
                    ? 'Sending...'
                    : 'Send invite'}
              </button>
            </div>

            <div
              className="border-t pt-[var(--sp-3)]"
              style={{ borderColor: 'var(--rd-border-subtle)' }}
            >
              <div className="flex flex-col sm:flex-row sm:items-center gap-[var(--sp-2)]">
                <span
                  className="text-[13px] font-medium sm:w-[80px]"
                  style={{ color: 'var(--rd-text-subtle)' }}
                >
                  Or share a link
                </span>
                {generatedLink ? (
                  <>
                    <input
                      type="text"
                      readOnly
                      value={generatedLink}
                      onFocus={(e) => e.currentTarget.select()}
                      className="h-[36px] px-[var(--sp-3)] rounded-[8px] text-[13px] border outline-none flex-1 min-w-0 font-mono"
                      style={{
                        background: 'var(--rd-surface-input)',
                        borderColor: 'var(--rd-border)',
                        color: 'var(--rd-text-strong)',
                      }}
                    />
                    <button
                      onClick={handleCopyLink}
                      className="h-[36px] px-[var(--sp-3)] rounded-[8px] text-[13px] font-semibold cursor-pointer border-0 text-white"
                      style={{
                        background: linkCopied
                          ? 'var(--rd-success-text)'
                          : 'var(--rd-text-strong)',
                      }}
                    >
                      {linkCopied ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                      onClick={handleGenerateLink}
                      disabled={linkGenerating}
                      className="h-[36px] px-[var(--sp-3)] rounded-[8px] text-[13px] font-semibold cursor-pointer border bg-transparent disabled:opacity-50"
                      style={{
                        borderColor: 'var(--rd-border)',
                        color: 'var(--rd-text-medium)',
                      }}
                    >
                      {linkGenerating ? 'Generating...' : 'New link'}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleGenerateLink}
                    disabled={linkGenerating}
                    className="h-[36px] px-[var(--sp-4)] rounded-[8px] text-[13px] font-semibold cursor-pointer border bg-transparent disabled:opacity-50"
                    style={{
                      borderColor: 'var(--rd-border)',
                      color: 'var(--rd-text-medium)',
                    }}
                  >
                    {linkGenerating ? 'Generating...' : 'Generate invite link'}
                  </button>
                )}
              </div>
              {generatedLink && (
                <p
                  className="text-[12px] mt-[var(--sp-2)] mb-0"
                  style={{ color: 'var(--rd-text-subtle)' }}
                >
                  Single-use link. Anyone who opens it joins as{' '}
                  <span style={{ color: 'var(--rd-text-strong)', fontWeight: 600 }}>
                    {formatRoleLabel(inviteRole)}
                  </span>
                  . Generate a new link for each person you invite.
                </p>
              )}
            </div>

            {inviteError && (
              <p
                className="text-[13px] mt-[var(--sp-3)] mb-0"
                style={{ color: 'var(--rd-danger-text)' }}
              >
                {inviteError}
              </p>
            )}
          </div>
        ) : null}

        <div
          className="rounded-[10px] border overflow-hidden"
          style={{
            background: 'var(--rd-surface-panel)',
            borderColor: 'var(--rd-border)',
          }}
        >
          {members.map((member, idx) => (
            <div
              key={member.id}
              className="flex items-center gap-[var(--sp-3)] px-[var(--sp-4)] py-[var(--sp-3)]"
              style={{
                borderBottom:
                  idx < members.length - 1
                    ? '1px solid var(--rd-border-subtle)'
                    : 'none',
              }}
            >
              <Avatar
                name={member.user.name}
                avatarUrl={member.user.avatar_url}
                size={28}
              />
              <div className="flex-1 min-w-0">
                <div
                  className="text-[13px] font-semibold"
                  style={{ color: 'var(--rd-text-strong)' }}
                >
                  {member.user.name}
                </div>
                <div
                  className="text-[13px]"
                  style={{ color: 'var(--rd-text-subtle)' }}
                >
                  {member.user.email}
                </div>
              </div>
              {canChangeRoles && member.id !== user.id ? (
                <select
                  value={member.role}
                  onChange={(e) => handleRoleChange(member.id, e.target.value)}
                  disabled={changingRole === member.id}
                  className="h-[32px] px-[var(--sp-2)] rounded-[8px] text-[13px] font-semibold border cursor-pointer"
                  style={{
                    background: 'var(--rd-surface-input)',
                    borderColor: 'var(--rd-border)',
                    color: 'var(--rd-text-medium)',
                  }}
                >
                  <option value="owner">Owner</option>
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                  <option value="viewer">Viewer</option>
                </select>
              ) : (
                <span
                  className="inline-flex items-center h-[28px] px-[var(--sp-2)] rounded-[var(--radius-full)] text-[13px] font-semibold"
                  style={{
                    background: 'var(--rd-surface-hover)',
                    color: 'var(--rd-text-medium)',
                  }}
                >
                  {formatRoleLabel(member.role)}
                </span>
              )}
            </div>
          ))}
        </div>
      </section>

      {canManageWorkspace && (
        <section className="mb-[var(--sp-8)]">
          <h2
            className="text-[16px] font-semibold mb-[var(--sp-4)]"
            style={{ color: 'var(--rd-text-strong)' }}
          >
            Connect Claude for the Organization
          </h2>
          <div
            className="rounded-[10px] p-[var(--sp-5)] border"
            style={{
              background: 'var(--rd-surface-panel)',
              borderColor: 'var(--rd-border)',
            }}
          >
            <p
              className="text-[14px] mb-[var(--sp-4)]"
              style={{ color: 'var(--rd-text-medium)' }}
            >
              Organization admins can add Exhibit as a shared connector for the workspace. Each user will still need to authenticate their own access afterwards.
            </p>

            <div
              className="rounded-[10px] p-[var(--sp-4)] mb-[var(--sp-3)]"
              style={{ background: 'var(--rd-surface-hover)' }}
            >
              <p className="text-[13px] font-semibold mb-[var(--sp-2)]" style={{ color: 'var(--rd-text-strong)' }}>
                Claude
              </p>
              <p className="text-[13px] mb-[var(--sp-2)]" style={{ color: 'var(--rd-text-subtle)' }}>
                Go to <span className="font-semibold">Organization Settings</span> → <span className="font-semibold">Connectors</span> → <span className="font-semibold">Add</span> → <span className="font-semibold">Custom</span> → <span className="font-semibold">Web</span>. Use <span className="font-semibold">Exhibit</span> as the name, then paste the MCP URL below.
              </p>
              <a
                href="https://claude.ai/admin-settings/connectors?modal=add-custom-connector"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-[var(--sp-2)] text-[13px] font-semibold no-underline"
                style={{ color: 'var(--rd-accent)' }}
              >
                Open Claude admin connector setup
                <i className="icon icon-app-open-in-new-window" />
              </a>
              <div className="mt-[var(--sp-3)] flex gap-[var(--sp-2)]">
                <div
                  className="flex-1 px-[var(--sp-3)] py-[var(--sp-2)] rounded-[8px] font-mono text-[13px] overflow-x-auto whitespace-nowrap"
                  style={{
                    background: 'var(--rd-text-strong)',
                    color: '#e2e8f0',
                  }}
                >
                  {mcpUrl}
                </div>
                <button
                  onClick={handleCopyMcpUrl}
                  className="h-[36px] px-[var(--sp-3)] rounded-[8px] text-[13px] font-semibold cursor-pointer border-0 text-white flex-shrink-0 transition-colors duration-[120ms]"
                  style={{ background: copiedMcpUrl ? 'var(--rd-success-text)' : 'var(--rd-text-strong)' }}
                >
                  {copiedMcpUrl ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {canManageWorkspace && (
        <section className="mb-[var(--sp-8)]">
          <h2
            className="text-[16px] font-semibold mb-[var(--sp-4)]"
            style={{ color: 'var(--rd-text-strong)' }}
          >
            Slack
          </h2>
          <div
            className="rounded-[10px] p-[var(--sp-5)] border"
            style={{
              background: 'var(--rd-surface-panel)',
              borderColor: 'var(--rd-border)',
            }}
          >
            {!slackStatus?.connected ? (
              <>
                <p
                  className="text-[14px] mb-[var(--sp-4)]"
                  style={{ color: 'var(--rd-text-medium)' }}
                >
                  Connect Slack to get DM notifications for comments, mentions, and shared artifacts. Links shared in Slack will show rich previews.
                </p>
                <a
                  href={`https://slack.com/oauth/v2/authorize?client_id=${SLACK_CLIENT_ID}&scope=chat:write,users:read,users:read.email,links:read,links:write&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/slack/callback')}&state=${btoa(JSON.stringify({ workspace_id: workspace?.id }))}`}
                >
                  <img
                    alt="Add to Slack"
                    height="40"
                    width="139"
                    src="https://platform.slack-edge.com/img/add_to_slack.png"
                    srcSet="https://platform.slack-edge.com/img/add_to_slack.png 1x, https://platform.slack-edge.com/img/add_to_slack@2x.png 2x"
                  />
                </a>
              </>
            ) : (
              <>
                <div
                  className="flex items-center gap-[var(--sp-3)] mb-[var(--sp-3)] p-[var(--sp-3)] rounded-[10px]"
                  style={{ background: 'var(--rd-surface-success)' }}
                >
                  <i className="icon icon-app-check" style={{ fontSize: 16, color: 'var(--rd-success-text)' }} />
                  <span className="text-[13px] font-semibold flex-1" style={{ color: 'var(--rd-success-text)' }}>
                    Connected to {slackStatus.slack_team_name || 'Slack'}
                  </span>
                  <button
                    onClick={handleDisconnectSlack}
                    disabled={slackDisconnecting}
                    className="text-[13px] font-semibold cursor-pointer border-0 bg-transparent"
                    style={{ color: 'var(--rd-danger-text)' }}
                  >
                    {slackDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                  </button>
                </div>
                <p className="text-[13px] mb-0" style={{ color: 'var(--rd-text-subtle)' }}>
                  {slackStatus.installed_by_name ? `Connected by ${slackStatus.installed_by_name}` : 'Connected'}
                  {slackStatus.installed_at ? ` · ${new Date(slackStatus.installed_at).toLocaleDateString()}` : ''}
                </p>
                <p className="text-[13px] mt-[var(--sp-3)] mb-0" style={{ color: 'var(--rd-text-medium)' }}>
                  Workspace members receive Slack DMs for comments, mentions, and shared artifacts. Exhibit links pasted in Slack show rich previews.
                </p>
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
